import React, { useState } from 'react';
import './BPMPickerModal.css';

/**
 * Props for BPMPickerModal component
 * 
 * Requirements: 3.4, 15.1, 15.2, 15.3, 15.6, 15.7
 */
export interface BPMPickerModalProps {
  /** Current BPM value */
  currentBPM: number;
  /** Callback when BPM is changed and confirmed */
  onBPMChange: (bpm: number) => void;
  /** Callback when modal is closed */
  onClose: () => void;
}

/**
 * BPMPickerModal Component
 * 
 * Centered modal for changing BPM value.
 * Displays as centered modal with z-index: 101.
 * Shows BPM input field or slider.
 * Validates BPM range (60-300).
 * Provides Cancel and Confirm buttons (44x44px minimum).
 * 
 * Requirements: 3.4, 15.1, 15.2, 15.3, 15.6, 15.7
 */
export const BPMPickerModal: React.FC<BPMPickerModalProps> = ({
  currentBPM,
  onBPMChange,
  onClose,
}) => {
  // Local state for BPM value before confirmation
  const [bpm, setBPM] = useState<number>(currentBPM);
  const [inputValue, setInputValue] = useState<string>(currentBPM.toString());
  const [error, setError] = useState<string>('');

  // BPM range constants - Requirement 15.2
  const MIN_BPM = 60;
  const MAX_BPM = 300;

  /**
   * Validate BPM value is within range
   * Requirement 15.2: Validate BPM range (60-300)
   */
  const validateBPM = (value: number): boolean => {
    return value >= MIN_BPM && value <= MAX_BPM;
  };

  /**
   * Handle slider change
   * Requirement 15.3: Provide slider for BPM adjustment
   */
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newBPM = parseInt(e.target.value, 10);
    setBPM(newBPM);
    setInputValue(newBPM.toString());
    setError('');
  };

  /**
   * Handle input field change
   * Requirement 15.3: Provide input field for BPM adjustment
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // Parse and validate
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) {
      setError('Please enter a valid number');
      return;
    }

    if (!validateBPM(numValue)) {
      setError(`BPM must be between ${MIN_BPM} and ${MAX_BPM}`);
    } else {
      setError('');
      setBPM(numValue);
    }
  };

  /**
   * Handle confirm button click
   * Requirement 15.6: Update composition on confirmation
   */
  const handleConfirm = () => {
    // Final validation before confirming
    if (!validateBPM(bpm)) {
      setError(`BPM must be between ${MIN_BPM} and ${MAX_BPM}`);
      return;
    }

    onBPMChange(bpm);
    onClose();
  };

  /**
   * Handle cancel button click
   * Requirement 15.7: Provide cancel button
   */
  const handleCancel = () => {
    onClose();
  };

  /**
   * Handle backdrop click to close modal
   */
  const handleBackdropClick = () => {
    onClose();
  };

  /**
   * Prevent modal content clicks from closing the modal
   */
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !error) {
      handleConfirm();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <>
      {/* Backdrop overlay - Requirement 15.1: Centered modal */}
      <div 
        className="bpm-picker-backdrop" 
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      
      {/* Modal container - Requirement 15.1: z-index: 101 */}
      <div 
        className="bpm-picker-modal" 
        onClick={handleModalClick}
        onKeyDown={handleKeyDown}
        role="dialog" 
        aria-label="BPM picker"
        aria-modal="true"
      >
        {/* Modal Header */}
        <div className="bpm-picker-header">
          <h3 className="bpm-picker-title">Set BPM</h3>
          <p className="bpm-picker-subtitle">
            Tempo: {MIN_BPM}-{MAX_BPM} beats per minute
          </p>
        </div>

        {/* Modal Content - Requirement 15.3: Input field and slider */}
        <div className="bpm-picker-content">
          {/* BPM Display */}
          <div className="bpm-display">
            <div className="bpm-value">{bpm}</div>
            <div className="bpm-label">BPM</div>
          </div>

          {/* BPM Input Field - Requirement 15.3 */}
          <div className="bpm-input-container">
            <label htmlFor="bpm-input" className="bpm-input-label">
              Enter BPM:
            </label>
            <input
              id="bpm-input"
              type="number"
              className={`bpm-input ${error ? 'error' : ''}`}
              min={MIN_BPM}
              max={MAX_BPM}
              value={inputValue}
              onChange={handleInputChange}
              aria-label="BPM value"
              aria-invalid={!!error}
              aria-describedby={error ? 'bpm-error' : undefined}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div id="bpm-error" className="bpm-error" role="alert">
              {error}
            </div>
          )}

          {/* BPM Slider - Requirement 15.3 */}
          <div className="bpm-slider-container">
            <span className="bpm-slider-label" aria-hidden="true">{MIN_BPM}</span>
            <input
              type="range"
              className="bpm-slider"
              min={MIN_BPM}
              max={MAX_BPM}
              step="1"
              value={bpm}
              onChange={handleSliderChange}
              aria-label="BPM slider"
              aria-valuemin={MIN_BPM}
              aria-valuemax={MAX_BPM}
              aria-valuenow={bpm}
              aria-valuetext={`${bpm} BPM`}
            />
            <span className="bpm-slider-label" aria-hidden="true">{MAX_BPM}</span>
          </div>
        </div>

        {/* Modal Footer - Requirements 15.6, 15.7: Cancel and Confirm buttons */}
        <div className="bpm-picker-footer">
          <button
            className="bpm-picker-btn bpm-picker-cancel"
            onClick={handleCancel}
            aria-label="Cancel BPM change"
            title="Cancel"
          >
            Cancel
          </button>
          <button
            className="bpm-picker-btn bpm-picker-confirm"
            onClick={handleConfirm}
            disabled={!!error}
            aria-label="Confirm BPM change"
            title="Confirm"
          >
            Confirm
          </button>
        </div>
      </div>
    </>
  );
};
